import React from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom';
import {
  NotFoundPage,
  PrivacyPolicyPage,
  TermsOfUsePage,
  DeleteAccountPage,
  InventoryOnboardingPage,
  InvoiceSharePage
} from '@/pages';
import RestaurantOrderParser from '@/pages/Home/comparison/RestaurantOrderParser';
import TokenRestaurantParser from '@/pages/Home/comparison/TokenRestaurantParser';
import RestaurantOrderResults from '@/pages/Home/comparison/RestaurantOrderResults';
import SupplierOrderParser from '@/pages/Home/comparison/SupplierOrderParser';
import { OrderResultsPage } from '@/pages/Home/comparison/SupplierOrderResults';
import LoginPage from '@/pages/Login/LoginPage';
import InviteAcceptPage from '@/pages/Invite/InviteAcceptPage';
import {
  SuperAdminLayout,
  SuperAdminDashboardPage,
  SuperAdminOrdersPage,
  SuperAdminSupplierOrderPage,
  SuperAdminProfilePage,
  SuperAdminRestaurantsPage,
  SuperAdminRestaurantDetailPage,
  SuperAdminSuppliersPage,
  SuperAdminSupplierDetailPage,
  SuperAdminSupplierCatalogPage,
  SuperAdminLocalizationsPage
} from '@/pages/SuperAdmin';
import { useAuth } from '@/shared/contexts';

const SuperAdminGuard: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const { isAuthenticated, authType, user, isLoading, logout } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading…
      </div>
    );
  }

  const isStrictSuperAdmin =
    isAuthenticated &&
    authType === 'super_admin' &&
    user?.role === 'super_admin' &&
    (!user.status || user.status === 'active');

  if (!isStrictSuperAdmin) {
    if (isAuthenticated && authType === 'super_admin') {
      logout();
    }
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const OrderParserRouter: React.FC = () => {
  const { isAuthenticated, authType } = useAuth();
  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);
  const tokenFromUrl = urlParams.get('token');

  // Prefer authenticated restaurant session (invite accept) over raw token parser.
  if (isAuthenticated && authType === 'restaurant') {
    return <RestaurantOrderParser />;
  }

  if (isAuthenticated && authType === 'supplier') {
    return <SupplierOrderParser />;
  }

  if (isAuthenticated && authType === 'super_admin') {
    return <Navigate to="/super-admin" replace />;
  }

  // Legacy: association access_token still in URL without session.
  if (tokenFromUrl) {
    return <TokenRestaurantParser />;
  }

  return <Navigate to="/login" replace />;
};

const RootRedirect = () => {
  const location = useLocation();
  const { isAuthenticated, authType, isLoading } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  const inventoryLink = searchParams.get('link');

  if (inventoryLink) {
    return <Navigate to={`/inventory/add${location.search}`} replace />;
  }

  if (token) {
    return <Navigate to={`/order-parser${location.search}`} replace />;
  }

  if (isLoading) {
    return null;
  }

  if (isAuthenticated) {
    if (authType === 'super_admin') {
      return <Navigate to="/super-admin" replace />;
    }
    if (authType === 'supplier' || authType === 'restaurant') {
      return <Navigate to="/order-parser" replace />;
    }
  }

  return <Navigate to="/login" replace />;
};

const InventoryOnboardingRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/inventory/add${location.search}`} replace />;
};

export const AppRouter = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<RootRedirect />} />

        <Route path="/login" element={<LoginPage />} />

        <Route path="/invite/accept" element={<InviteAcceptPage />} />

        <Route path="/order-parser" element={<OrderParserRouter />} />

        <Route path="/supplier-order-results" element={<OrderResultsPage />} />

        <Route
          path="/restaurant-order-results"
          element={<RestaurantOrderResults />}
        />

        {/* Public inventory onboarding — no auth/login/super-admin redirects */}
        <Route path="/inventory/add" element={<InventoryOnboardingPage />} />
        <Route
          path="/inventory/onboarding"
          element={<InventoryOnboardingRedirect />}
        />

        <Route
          path="/super-admin"
          element={
            <SuperAdminGuard>
              <SuperAdminLayout />
            </SuperAdminGuard>
          }
        >
          <Route index element={<SuperAdminDashboardPage />} />
          <Route path="restaurants" element={<SuperAdminRestaurantsPage />} />
          <Route
            path="restaurants/:restaurantId"
            element={<SuperAdminRestaurantDetailPage />}
          />
          <Route path="suppliers" element={<SuperAdminSuppliersPage />} />
          <Route
            path="suppliers/:supplierId"
            element={<SuperAdminSupplierDetailPage />}
          />
          <Route
            path="suppliers/:supplierId/catalog"
            element={<SuperAdminSupplierCatalogPage />}
          />
          <Route
            path="suppliers/:supplierId/orders"
            element={<SuperAdminSupplierOrderPage />}
          />
          <Route path="orders" element={<SuperAdminOrdersPage />} />
          <Route
            path="orders/:supplierId"
            element={<SuperAdminSupplierOrderPage />}
          />
          <Route path="localizations" element={<SuperAdminLocalizationsPage />} />
          <Route path="profile" element={<SuperAdminProfilePage />} />
        </Route>

        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-use" element={<TermsOfUsePage />} />
        <Route path="/delete-account" element={<DeleteAccountPage />} />

        {/* Public permanent invoice share — no auth */}
        <Route path="/invoice/:token" element={<InvoiceSharePage />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
};
